import { useState, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from '@/common/ui/card'
import { Button } from '@/common/ui/button'
import { Upload, Check, X } from 'lucide-react'
import useMutationUploadFavicon from '@/core/api/hooks/useMutationUploadFavicon'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import useQueryVersionedUrl from '@/common/hooks/useQueryVersionedUrl'

interface FaviconUploadBoxProps {
	projectName: string
	currentFaviconUrl?: string
	onFaviconUploaded?: (faviconUrl: string) => void
}

export default function FaviconUploadBox({
	projectName,
	currentFaviconUrl,
	onFaviconUploaded,
}: FaviconUploadBoxProps) {
	// Generate default favicon URL
	const defaultFaviconUrl = `https://image.panda-storage.com/${projectName}/config/apple-touch-icon.png`
	const faviconUrlToFetch = currentFaviconUrl || defaultFaviconUrl

	const queryClient = useQueryClient()
	const { data: faviconUrl, isLoading: isLoadingImage } =
		useQueryVersionedUrl(faviconUrlToFetch)

	const [isUploading, setIsUploading] = useState(false)
	const [previewUrl, setPreviewUrl] = useState<string>('')
	const [selectedFile, setSelectedFile] = useState<File | null>(null)
	const fileInputRef = useRef<HTMLInputElement>(null)

	const { mutateAsync: uploadFavicon } = useMutationUploadFavicon()

	const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0]
		if (!file) return

		// Validate file type - only PNG
		if (file.type !== 'image/png') {
			showErrorToast('กรุณาเลือกไฟล์ PNG เท่านั้น')
			return
		}

		// Validate file size (10MB max)
		const maxSize = 10 * 1024 * 1024
		if (file.size > maxSize) {
			showErrorToast('ขนาดไฟล์ใหญ่เกินไป (สูงสุด 10MB)')
			return
		}

		// Store the file for later upload
		setSelectedFile(file)

		// Create preview
		const reader = new FileReader()
		reader.onload = (e) => {
			setPreviewUrl(e.target?.result as string)
		}
		reader.readAsDataURL(file)
	}

	const handleConfirmUpload = async () => {
		if (!selectedFile) return

		try {
			setIsUploading(true)

			// Upload favicon directly to backend (backend handles all sizes and cache invalidation)
			const result = await uploadFavicon(selectedFile)

			// Invalidate query to refetch with new ETag
			await queryClient.invalidateQueries({
				queryKey: ['versionedUrl', faviconUrlToFetch],
			})

			// Clear preview and selected file after successful upload
			setPreviewUrl('')
			setSelectedFile(null)

			// Call callback if provided
			if (onFaviconUploaded) {
				onFaviconUploaded(result.appleIcon)
			}

			toast.success(
				'Favicon ถูกอัปโหลดและสร้างในรูปแบบต่างๆ สำเร็จ กรุณารอสักครู่เพื่อให้การเปลี่ยนแปลงมีผลในระบบ',
			)

			// Reset file input
			if (fileInputRef.current) {
				fileInputRef.current.value = ''
			}
		} catch (error) {
			console.error('Favicon upload failed:', error)
			showErrorToast('ไม่สามารถอัปโหลด favicon ได้ กรุณาลองใหม่อีกครั้ง')
		} finally {
			setIsUploading(false)
		}
	}

	const handleClearPreview = () => {
		setPreviewUrl('')
		setSelectedFile(null)
		if (fileInputRef.current) {
			fileInputRef.current.value = ''
		}
	}

	const handleUploadClick = () => {
		fileInputRef.current?.click()
	}

	return (
		<Card>
			<CardHeader>
				<CardTitle>ตั้งค่า Favicon สำหรับเว็บไซต์</CardTitle>
				<CardDescription>
					อัปโหลด favicon ใหม่สำหรับ {projectName}
				</CardDescription>
			</CardHeader>
			<CardContent className="space-y-4">
				{/* Loading state for initial image */}
				{isLoadingImage && !previewUrl && (
					<div className="flex flex-col items-center space-y-2">
						<div className="flex h-16 w-16 items-center justify-center rounded-lg border bg-muted/50 p-4">
							<p className="text-xs text-muted-foreground">กำลังโหลด...</p>
						</div>
					</div>
				)}

				{/* Display current favicon */}
				{faviconUrl && !previewUrl && !isLoadingImage && (
					<div className="flex flex-col items-center space-y-2">
						<div className="flex items-center justify-center rounded-lg border bg-muted/50 p-4">
							<img
								src={faviconUrl}
								alt="Current Favicon"
								className="h-16 w-16 object-contain"
							/>
						</div>
						<p className="text-sm text-muted-foreground">Favicon ปัจจุบัน</p>
					</div>
				)}

				{/* Display preview */}
				{previewUrl && (
					<div className="flex flex-col items-center space-y-2">
						<div className="flex items-center justify-center rounded-lg border-2 border-primary/50 bg-muted/50 p-4">
							<img
								src={previewUrl}
								alt="Preview Favicon"
								className="h-16 w-16 object-contain"
							/>
						</div>
						<p className="text-sm font-medium text-primary">
							ตัวอย่าง Favicon ใหม่
						</p>
					</div>
				)}

				{/* Action buttons */}
				{!previewUrl ? (
					<Button
						type="button"
						variant="outline"
						size="sm"
						className="w-full"
						onClick={handleUploadClick}
						disabled={isUploading || isLoadingImage}
					>
						<Upload className="mr-2 h-4 w-4" />
						{faviconUrl ? 'เปลี่ยน Favicon' : 'อัปโหลด Favicon ใหม่'}
					</Button>
				) : (
					<div className="flex gap-2">
						<Button
							type="button"
							variant="default"
							size="sm"
							className="flex-1"
							onClick={handleConfirmUpload}
							disabled={isUploading}
						>
							<Check className="mr-2 h-4 w-4" />
							ยืนยันการอัปโหลด
						</Button>
						<Button
							type="button"
							variant="outline"
							size="sm"
							className="flex-1"
							onClick={handleClearPreview}
							disabled={isUploading}
						>
							<X className="mr-2 h-4 w-4" />
							ยกเลิก
						</Button>
					</div>
				)}

				{/* Hidden file input */}
				<input
					ref={fileInputRef}
					type="file"
					accept="image/png"
					onChange={handleFileSelect}
					className="hidden"
					disabled={isUploading}
				/>

				{/* Loading state */}
				{isUploading && (
					<p className="text-center text-sm text-muted-foreground">
						กำลังอัปโหลด...
					</p>
				)}

				{/* Help text */}
				{!previewUrl && (
					<p className="text-sm text-muted-foreground">
						รองรับไฟล์ PNG เท่านั้น
						(ระบบหลังบ้านจะทำการปรับขนาดและเปลี่ยนเป็นไฟล์ ICO ให้เอง)
					</p>
				)}
			</CardContent>
		</Card>
	)
}
